/**
 * Editor Screen
 * Create or edit a list. Mode is selected by params.listId presence.
 */

import Sortable from 'sortablejs';
import {
  getListById,
  createList,
  updateList,
  deleteList
} from '../lists.js';
import {
  hasActiveSessionForList,
  clearSession
} from '../session.js';
import { showConfirmDialog } from '../ui/modal-stubs.js';

function escapeHtml(s) {
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

export function reorderItems(items, fromIndex, toIndex) {
  if (fromIndex === toIndex) return [...items];
  const next = [...items];
  const [moved] = next.splice(fromIndex, 1);
  next.splice(toIndex, 0, moved);
  return next;
}

export function renderEditor(root, params, api) {
  const editingId = params && params.listId ? params.listId : null;

  let originalList = null;
  if (editingId) {
    originalList = getListById(editingId);
    if (!originalList) {
      api.navigateTo('home', {});
      return;
    }
  }

  const state = {
    name: originalList ? originalList.name : '',
    items: originalList ? [...originalList.items] : []
  };

  let sortable = null;

  function isSaveEnabled() {
    return state.name.trim().length > 0 && state.items.length > 0;
  }

  doRender();

  function doRender() {
    if (sortable) {
      try { sortable.destroy(); } catch (_e) { /* noop */ }
      sortable = null;
    }

    root.innerHTML = `
      <main class="screen screen-editor">
        <header class="editor-header">
          <button type="button" data-action="cancel" class="editor-cancel"
                  aria-label="Cancel and return to home">Cancel</button>
          <h1 tabindex="-1">${editingId ? 'Edit List' : 'New List'}</h1>
          <button type="button" data-action="save" class="editor-save"
                  aria-label="Save list"${isSaveEnabled() ? '' : ' disabled'}>
            Save
          </button>
        </header>

        <label class="editor-name-label">
          <span class="muted">Name</span>
          <input type="text" data-field="name" value="${escapeHtml(state.name)}"
                 placeholder="List name" autocomplete="off"
                 aria-label="List name" />
        </label>

        <ul class="editor-items" aria-label="List items">
          ${state.items.map((item, idx) => `
            <li class="editor-item-row" data-index="${idx}">
              <span class="editor-item-row__handle" aria-hidden="true">⋮⋮</span>
              <span class="editor-item-row__text">${escapeHtml(item)}</span>
              <button type="button" data-action="delete-item" data-index="${idx}"
                      aria-label="Delete item ${escapeHtml(item)}">×</button>
            </li>
          `).join('')}
        </ul>

        <div class="editor-add-item">
          <input type="text" data-field="add-item" placeholder="Add item"
                 aria-label="New item" autocomplete="off" />
          <button type="button" data-action="add-item-commit"
                  aria-label="Add item">+</button>
        </div>

        ${editingId ? `
          <button type="button" data-action="delete-list" class="editor-delete-list">
            Delete List
          </button>
        ` : ''}
      </main>
    `;

    wireEvents();

    const itemsEl = root.querySelector('.editor-items');
    if (itemsEl && state.items.length > 0) {
      sortable = new Sortable(itemsEl, {
        handle: '.editor-item-row__handle',
        animation: 150,
        onEnd: (evt) => {
          if (typeof evt.oldIndex !== 'number' || typeof evt.newIndex !== 'number') return;
          state.items = reorderItems(state.items, evt.oldIndex, evt.newIndex);
          doRender();
        }
      });
    }
  }

  function wireEvents() {
    const nameInput = root.querySelector('[data-field="name"]');
    nameInput.addEventListener('input', (e) => {
      state.name = e.target.value;
      syncSaveDisabled();
    });

    const addInput = root.querySelector('[data-field="add-item"]');
    const commitAdd = () => {
      const trimmed = addInput.value.trim();
      if (trimmed.length === 0) return;
      state.items.push(trimmed);
      doRender();
      const refreshedAdd = root.querySelector('[data-field="add-item"]');
      refreshedAdd.value = '';
      refreshedAdd.focus();
    };
    addInput.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        commitAdd();
      }
    });
    const addBtn = root.querySelector('[data-action="add-item-commit"]');
    addBtn.addEventListener('click', commitAdd);

    for (const btn of root.querySelectorAll('[data-action="delete-item"]')) {
      btn.addEventListener('click', () => {
        const idx = Number(btn.getAttribute('data-index'));
        const snapshotBefore = [...state.items];
        const [removed] = state.items.splice(idx, 1);
        doRender();
        api.showToast(`Deleted "${removed}"`, {
          variant: 'info',
          duration: 4000,
          action: {
            label: 'Undo',
            onClick: () => {
              state.items = [...snapshotBefore];
              doRender();
            }
          }
        });
      });
    }

    root.querySelector('[data-action="cancel"]').addEventListener('click', () => {
      api.navigateTo('home', {});
    });

    root.querySelector('[data-action="save"]').addEventListener('click', () => {
      if (!isSaveEnabled()) return;

      const proceed = () => {
        if (!isSaveEnabled()) return;
        const trimmedName = state.name.trim();
        if (editingId) {
          updateList(editingId, { name: trimmedName, items: [...state.items] });
        } else {
          createList({ name: trimmedName, items: [...state.items] });
        }
        api.navigateTo('home', {});
      };

      if (editingId && hasActiveSessionForList(editingId)) {
        showConfirmDialog({
          message: 'A run is in progress. Discard the run to continue?',
          confirmLabel: 'Discard run',
          cancelLabel: 'Cancel',
          onConfirm: () => {
            clearSession();
            proceed();
          }
        });
        return;
      }

      proceed();
    });

    const deleteListBtn = root.querySelector('[data-action="delete-list"]');
    if (deleteListBtn) {
      deleteListBtn.addEventListener('click', () => {
        const name = originalList ? originalList.name : '';
        showConfirmDialog({
          message: `Delete "${name}"? This can't be undone.`,
          confirmLabel: 'Delete',
          cancelLabel: 'Cancel',
          onConfirm: () => {
            deleteList(editingId);
            api.navigateTo('home', {});
          }
        });
      });
    }
  }

  function syncSaveDisabled() {
    const save = root.querySelector('[data-action="save"]');
    if (!save) return;
    save.disabled = !isSaveEnabled();
  }
}
